import { useCallback, useRef, useState } from 'react';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';
import { getApiErrorMessage } from '../api/runtimeErrors';
import { useSurveyUiToast } from '../hooks/useSurveyUiToast';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Failed to read file.'));
    reader.readAsDataURL(file);
  });
}

/** Client/brand logo uploader — single image, shown contained in a fixed frame.
 * One logo per SDA tenant (tenantId), used automatically by every survey that
 * tenant owns — not per-survey. */
export default function SurveyLogoEditor({ tenantId, logoImage, onChange }) {
  const { apiFetch } = useSurveyRuntimeApi();
  const { showError } = useSurveyUiToast();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pickFile = useCallback(() => fileInputRef.current?.click(), []);

  const onFileSelected = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      if (!ALLOWED_TYPES.includes(file.type)) {
        showError('Only PNG, JPEG, and WEBP images are supported.');
        return;
      }
      if (file.size > MAX_BYTES) {
        showError('Image is too large (max 5 MB).');
        return;
      }
      setUploading(true);
      try {
        const dataUrl = await readFileAsDataUrl(file);
        const result = await apiFetch(`/admin/clients/${tenantId}/logo`, {
          method: 'PUT',
          body: JSON.stringify({ image_base64: dataUrl, mime_type: file.type }),
        });
        onChange(result);
      } catch (err) {
        showError(getApiErrorMessage(err, 'Failed to upload logo.'));
      } finally {
        setUploading(false);
      }
    },
    [apiFetch, onChange, showError, tenantId],
  );

  const removeLogo = useCallback(async () => {
    setUploading(true);
    try {
      const result = await apiFetch(`/admin/clients/${tenantId}/logo`, { method: 'DELETE' });
      onChange(result);
    } catch (err) {
      showError(getApiErrorMessage(err, 'Failed to remove logo.'));
    } finally {
      setUploading(false);
    }
  }, [apiFetch, onChange, showError, tenantId]);

  return (
    <div className="logo-editor">
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={onFileSelected}
      />
      <div className={`logo-frame${logoImage ? ' has-image' : ''}`}>
        {logoImage ? (
          <img src={logoImage} alt="Client logo" className="logo-frame-img" />
        ) : (
          <button type="button" className="logo-frame-empty" onClick={pickFile} disabled={uploading}>
            <span aria-hidden="true">＋</span>
          </button>
        )}
      </div>
      <div className="logo-editor-actions">
        <button type="button" className="btn small-btn" onClick={pickFile} disabled={uploading}>
          {uploading ? 'Uploading…' : logoImage ? 'Replace' : 'Add client logo'}
        </button>
        {logoImage && (
          <button type="button" className="btn small-btn danger" onClick={removeLogo} disabled={uploading}>
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
